import { LookingForSession } from "@/components/LookingForSession";
/**
 * SimpleLauncher (iter76) — Apple-style tile launcher for Simple Mode.
 *
 * Four large, high-contrast tiles route to the primary flows:
 *   · Book a Session   → /offerings
 *   · My Progress      → /progress
 *   · My Calendar      → /calendar
 *   · My Coaches       → /find-coach
 *
 * Rendered when `useUserMode().isSimple === true` for students. Coaches +
 * admins bypass this because the UserMode provider force-swaps them to
 * "advanced".
 *
 * Sidebar is hidden by Layout in simple mode; a minimal top-right control
 * cluster (theme toggle, notification bell, sign-out shortcut) keeps the
 * canvas uncluttered.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, CalendarPlus, ClipboardCheck, TrendingUp, UserCog, Settings2, Trophy, MapPin, Zap, LogOut, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useUserMode } from "@/context/UserModeContext";
import OnboardingCoachmark from "@/components/OnboardingCoachmark";
import InstallAppPrompt from "@/components/InstallAppPrompt";
import api from "@/lib/api";
import { SimpleMySessions } from "@/components/SimpleMySessions";
import "./SimpleLauncher.css";

const TILES = [
  {
    key: "book",
    to: "/offerings",
    label: "Book a Session",
    hint: "Browse sessions and grab a spot.",
    Icon: CalendarPlus,
    accent: "var(--volt)",
    testid: "simple-tile-book",
  },
  {
    // Iter132 — Restored "Request a Session" chooser step so Simple Mode
    // gives players an explicit Solo / Group / Club fork. Club still
    // routes to the full advanced form; Solo & Group get bare-bones
    // versions of it. Chooser is the same one Advanced Mode links to
    // from the Session Requests page.
    key: "request",
    to: "/request/new",
    label: "Request a Session",
    hint: "Solo · Group · Club — pick one.",
    Icon: ClipboardCheck,
    accent: "var(--teal)",
    testid: "simple-tile-request",
  },
  {
    key: "progress",
    to: "/progress",
    label: "My Progress",
    hint: "Check your skill map and next focus.",
    Icon: TrendingUp,
    accent: "var(--volt)",
    testid: "simple-tile-progress",
  },
  {
    key: "calendar",
    to: "/calendar",
    label: "My Calendar",
    hint: "Sessions you've booked. Never miss one.",
    Icon: Calendar,
    accent: "var(--azure)",
    testid: "simple-tile-calendar",
  },
  {
    key: "coaches",
    to: "/find-coach",
    label: "My Coaches",
    hint: "Chat with your coach or find a new one.",
    Icon: UserCog,
    accent: "var(--orange)",
    testid: "simple-tile-coaches",
  },
  {
    key: "trophies",
    to: "/badges",
    label: "My Trophies",
    hint: "Trophy case + badges you've earned.",
    Icon: Trophy,
    accent: "#FFD700",
    testid: "simple-tile-trophies",
  },
];

export default function SimpleLauncher() {
  const { user, logout } = useAuth();
  const { toggle } = useUserMode();
  const firstName = (user?.name || "").split(" ")[0] || "there";

  // Iter94d — Simple Mode was missing an explicit sign-out. Users had no
  // way to log out without first switching to Advanced Mode. Now the footer
  // shows both actions side-by-side.
  const handleSignOut = async () => {
    if (!window.confirm("Sign out of Rally Lab?")) return;
    try { await logout(); } catch (_e) { /* logout swallows already */ }
  };

  // Iter94 — Fetch credit balance so Simple-Mode students see their credits
  // without having to switch to Advanced. Silent failure (endpoint 403 for
  // non-students on some accounts) keeps the launcher usable regardless.
  const [credits, setCredits] = useState({ total_remaining: 0, next_expiry: null });
  useEffect(() => {
    let cancelled = false;
    api.get("/me/credits").then((r) => { if (!cancelled) setCredits(r.data || { total_remaining: 0, next_expiry: null }); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="simple-launcher" data-testid="simple-launcher">
      <header className="simple-launcher__hero">
        <div className="simple-launcher__eyebrow">RALLY LAB</div>
        <h1 className="simple-launcher__title">
          Hey {firstName}. <span className="accent">What&apos;s next?</span>
        </h1>
        <p className="simple-launcher__sub">
          Pick a card to dive in. You&apos;re in <strong>Simple Mode</strong> — everything you need, nothing you don&apos;t.
        </p>
        <InstallAppPrompt />
        {credits.total_remaining > 0 && (
          <Link
            to="/credits"
            data-testid="simple-credits-widget"
            className="simple-launcher__credits"
          >
            <span className="simple-launcher__credits-icon"><Zap /></span>
            <span className="simple-launcher__credits-body">
              <span className="simple-launcher__credits-total" data-testid="simple-credits-total">{credits.total_remaining}</span>
              <span className="simple-launcher__credits-label">credit{credits.total_remaining === 1 ? "" : "s"} remaining</span>
            </span>
            {credits.next_expiry && (
              <span className="simple-launcher__credits-expiry" data-testid="simple-credits-expiry">
                expires {new Date(credits.next_expiry).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
              </span>
            )}
          </Link>
        )}
      </header>

      <SimpleMySessions />

      <div style={{ marginBottom: 32 }}><LookingForSession /></div>

      <div className="simple-launcher__grid" role="menu" aria-label="Main navigation tiles">
        {TILES.map(({ key, to, label, hint, Icon, accent, testid }) => (
          <Link
            key={key}
            to={to}
            data-testid={testid}
            role="menuitem"
            className="simple-tile"
            style={{ "--tile-accent": accent }}
          >
            <span className="simple-tile__icon" aria-hidden>
              <Icon strokeWidth={1.5} />
            </span>
            <span className="simple-tile__label">{label}</span>
            <span className="simple-tile__hint">{hint}</span>
            <span className="simple-tile__arrow" aria-hidden>→</span>
          </Link>
        ))}
      </div>

      <footer className="simple-launcher__foot">
        <div className="simple-launcher__foot-actions">
          <button
            type="button"
            onClick={toggle}
            data-testid="switch-to-advanced-btn"
            className="simple-launcher__switch"
          >
            <Settings2 className="w-4 h-4" strokeWidth={1.75} />
            Switch to Advanced Mode
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            data-testid="simple-sign-out-btn"
            className="simple-launcher__signout"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.75} />
            Sign out
          </button>
        </div>
        {user?.email && (
          <p className="simple-launcher__whoami" data-testid="simple-signed-in-as">
            Signed in as <strong>{user.email}</strong>
          </p>
        )}
        <p className="simple-launcher__disclaimer">
          Advanced Mode shows the full sidebar, admin panels and every feature. You can switch back anytime.
        </p>
      </footer>

      <OnboardingCoachmark name="simple_launcher">
        <strong>Welcome to Rally Lab.</strong> Tap any card to jump in — this is your home base.
        Need the full sidebar and every feature? Hit <em>Switch to Advanced Mode</em> below (or the switch at the bottom of any page).
      </OnboardingCoachmark>
    </div>
  );
}
